import { trpc } from '@/main'
import { createFileRoute } from '@tanstack/react-router'
import { noop } from '@tanstack/react-query'
import { jobIntegrantsSource } from '@/hooks/use-job'
import { reportSearchDeps, reportQueryOptions, PANES_WITH_OWN_QUERY } from '@/hooks/use-report'
import { deriveProfiles } from '@/hooks/use-root-search'

/**
 * Data-owning layout for a selected job (`/jobs/$jobId/*`): the jobId-keyed chrome (getJob + integrants list) and
 * the report data all three tab children read, declared here so the leaves keep only their own queries. NO
 * component, deliberately: with nothing to code-split there is no chunk for the router to await before the leaf's
 * skeleton can paint, and a re-key on the report search paints nothing here. The auto-open dialog for an incomplete
 * job lives in jobs.tsx (IncompleteJobAutoOpen), reading this match.
 */
export const Route = createFileRoute('/_app/jobs/_list/$jobId')({
  // EXACTLY the search this layout's context reads. `reportSearchDeps` already covers `first`/`second`/
  // `type` (the report keys); `o`/`od` are the list's own sort. Anything else changing must not re-run it.
  loaderDeps: ({ search }) => ({ ...reportSearchDeps(search), o: search.o, od: search.od, panelQ: search.panelQ }),
  context: ({ deps, params }) => ({
    // The detail every pane on this screen shows. The chrome ABOVE this match (breadcrumb, screen
    // title, the integrants panel) reads THIS field off the match rather than rebuilding the key.
    jobQueryOptions: trpc.jobs.getJob.queryOptions({ jobId: Number(params.jobId) }),
    // The integrants list keyed on the pane's OWN search param. The panel that renders it sits in the
    // parent `jobs.tsx`, above this match, so it reads THIS field off the match — its search box writes
    // `panelQ` (not `q`, which the rail beside it owns), so there is one key, not a searched variant.
    jobIntegrantsInfiniteQueryOptions: trpc.jobs.getJobIntegrants.infiniteQueryOptions(
      {
        jobId: Number(params.jobId),
        type: deps.type,
        o: deps.o,
        od: deps.od,
        q: deps.panelQ,
        employeesOnly: true,
        source: jobIntegrantsSource(deriveProfiles(deps, params, false).first),
      },
      { getNextPageParam: (lastPage) => lastPage.nextCursor },
    ),
    reportQueryOptions: reportQueryOptions(deps, params),
    // The board/distribution panes' indicators, owned HERE so an ámbito switch re-keys THIS match rather than
    // a leaf's: a leaf at `pendingMs: 0` paints its skeleton the moment its match reloads, and this layout has
    // no pendingComponent at all. Only the PREFETCH is gated on the pane below (see the loader) — the key
    // still lives here, so both readers get the one object.
    jobIntegrantIndicatorsQueryOptions: trpc.companies.getJobIntegrantIndicators.queryOptions({
      jobId: Number(params.jobId),
      type: deps.type,
    }),
  }),
  loader: ({ context, location }) => {
    context.queryClient.query(context.jobQueryOptions).catch(noop)
    context.queryClient.infiniteQuery(context.jobIntegrantsInfiniteQueryOptions).catch(noop)
    if (context.reportQueryOptions) context.queryClient.query(context.reportQueryOptions).catch(noop)
    // Board and distribution are the ONLY readers, and this layout also sits under /report and the index —
    // where warming it is a request per visit that nothing consumes. Gated on the pane rather than moved to
    // the leaves: the declaration has to stay here (see above), and `defaultPreloadStaleTime: 0` re-runs this
    // loader on the hover that preloads the tab, so the pane that does read it is still warmed by a loader.
    if (PANES_WITH_OWN_QUERY.test(location.pathname)) {
      context.queryClient.query(context.jobIntegrantIndicatorsQueryOptions).catch(noop)
    }
  },
})
